import React, { useEffect, useState } from "react";
import OrganizationDetails from "./OrganizationDetails";
import ContactDetails from "./ContactDetails";
import { ReactComponent as BackIcon } from "../../../assets/svgs/back-button.svg";
import TextStyles from "../../../components/Elements/TextStyles/TextStyles";
import { useNavigate } from "react-router-dom";
import CardsBorder from "../../../components/Cards/CardsBorder";
import Input from "../../../components/Elements/InputField/Input";
import CustomButton from "../../../components/Elements/Buttons/CustomButtons";
import { postApi } from "../../../api/api_instance";
import { API_URLS } from "../../../api/apiUrls";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard";
import { Check } from "lucide-react";

const NewOnBoarding = ({}) => {
  const navigate = useNavigate();
  const [basicInformation, setBasicInformation] = useState({
    name: "",
    location: "",
    username: "",
    email: "",
    otp: "",
    password: "",
    contactNumber_1: "",
    emailId_1: "",
    contactNumber_2: "",
    emailId_2: "",
    primaryContactNumber: "",
    description: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    otp: "",
    password: "",
  });

  const [otpSent, setOtpSent] = useState(false);
  const [otpSentSuccess, setOtpSentSuccess] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpVerifySuccess, setOtpVerifySuccess] = useState(false);
  const [verifyFailedMsg, setVerifyFailedMsg] = useState("");
  const [otpVerifyFailed, setOptVerifyFailed] = useState(false);
  const [saveFailedDialog, setSaveFailedDialog] = useState({
    open: false,
    message: "",
  });
  const [userCreatedSuccessfully, setUserCreatedSuccessfully] = useState(false);
  const [popup, setPopup] = useState({
    visible: false,
    type: "",
    title: "",
    message: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumbers: false,
    hasSpecialChar: false,
  });

  useEffect(() => {
    validatePasswordStrength(basicInformation.password);
  }, [basicInformation.password]);

  const validatePasswordStrength = (password) => {
    setPasswordStrength({
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumbers: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  const showPopup = (type, title, message, shouldNavigate = false) => {
    setPopup({ visible: true, type, title, message });
    setTimeout(() => {
      setPopup({ visible: false, type: "", title: "", message: "" });
      if (shouldNavigate) {
        navigate("/customer");
      }
    }, 3000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBasicInformation((prev) => ({ ...prev, [name]: value }));
    // clear per-field error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateEmailField = () => {
    if (!basicInformation.email.trim()) {
      setErrors((prev) => ({
        ...prev,
        email: "Email is required.",
      }));
      return false;
    }
    if (
      !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
        basicInformation.email
      )
    ) {
      showPopup(
        "warning",
        "Invalid Email",
        "Incorrect or Invalid Email Format"
      );

      return false;
    }
    return true;
  };

  const validateFields = () => {
    const newErrors = {};
    if (!basicInformation.name.trim()) newErrors.name = "Name is required.";
    if (!validateEmailField()) return false;
    if (!basicInformation.otp.trim()) newErrors.otp = "OTP is required.";
    if (!basicInformation.password.trim()) {
      newErrors.password = "Password is required.";
    } else {
      if (!passwordStrength.minLength)
        newErrors.password = "Password must be at least 8 characters.";
      if (!passwordStrength.hasUpperCase)
        newErrors.password = "Password must include an uppercase letter.";
      if (!passwordStrength.hasLowerCase)
        newErrors.password = "Password must include a lowercase letter.";
      if (!passwordStrength.hasNumbers)
        newErrors.password = "Password must include a number.";
      if (!passwordStrength.hasSpecialChar)
        newErrors.password = "Password must include a special character.";
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    try {
      // validate fields
      const isValid = validateFields();
      if (!isValid) return;

      if (!otpVerified) {
        showPopup(
          "warning",
          "OTP not verified",
          "Verify OTP before creating new customer!"
        );

        // setSaveFailedDialog({
        //   open: true,
        //   message: "Verify OTP before creating new customer!",
        // });
        return;
      }

      const formData = new FormData();
      formData.append("name", basicInformation.name);
      formData.append("email", basicInformation.email);
      formData.append("otp", basicInformation.otp);
      formData.append("password", basicInformation.password);

      const response = await postApi({
        apiUrl: API_URLS.ADD_NEW_CUSTOMER,
        data: formData,
      });
      if (response.success) {
        // setUserCreatedSuccessfully(true);
        showPopup(
          "success",
          "Customer Created",
          "New customer added successfully.",
          true
        );
      } else {
        showPopup(
          "error",
          "Save Failed",
          response.message || "Failed to create customer."
        );
      }
      console.log("Onboarding success:", response.data);
    } catch (err) {
      showPopup(
        "error",
        "Save Failed",
        err.message || "Failed to create customer."
      );

      setSaveFailedDialog({
        open: true,
        message: "Failed to save: " + err.message,
      });
    }
  };

  const handlePrimarySelection = (contactKey) => {
    const number =
      contactKey === "contact_1"
        ? basicInformation.contactNumber_1
        : basicInformation.contactNumber_2;
    setBasicInformation((prev) => ({ ...prev, primaryContactNumber: number }));
  };

  const handleSendCode = async () => {
    if (!validateEmailField()) return;
    if (!basicInformation.email) {
      setErrors((prev) => ({
        ...prev,
        email: "Email is required to send OTP.",
      }));
      return;
    }
    try {
      const response = await postApi({
        apiUrl: API_URLS.CUSTOMER_OTP_GENERATE,
        data: {
          email: basicInformation.email,
        },
      });
      if (response.success) {
        setOtpSent(true);
        setOtpSentSuccess(true);
        showPopup(
          "success",
          response.message || "OTP Sent",
          "Check your email and verify the OTP."
        );
      } else {
        showPopup(
          "error",
          "Failed to Send OTP",
          response.message || "Failed to send OTP."
        );
      }
    } catch (err) {
      showPopup(
        "error",
        "Failed to Send OTP",
        err.message || "Failed to send OTP."
      );
      setSaveFailedDialog({
        open: true,
        message: "Failed to send OTP: " + err.message,
      });
    }
  };

  const handleVerify = async () => {
    if (!basicInformation.otp.trim() || !basicInformation.email.trim()) {
      if (!basicInformation.email.trim()) {
        setErrors((prev) => ({ ...prev, email: "Email is required." }));
      }
      if (!basicInformation.otp.trim()) {
        setErrors((prev) => ({ ...prev, otp: "OTP is required." }));
      }
      return;
    }
    try {
      const response = await postApi({
        apiUrl: API_URLS.CUSTOMER_VERIFY_OTP,
        data: {
          OTP: basicInformation.otp,
          email: basicInformation.email,
          user_type: "buyer",
        },
      });

      if (response.success) {
        setOtpVerified(true);
        setOtpVerifySuccess(true);
        setOptVerifyFailed(false);
        showPopup("success", "OTP Verified", "OTP verified successfully.");
      } else {
        setVerifyFailedMsg(response.message || "Verification failed");
        setOptVerifyFailed(true);
        setOtpVerifySuccess(false);
        showPopup(
          "error",
          "OTP Verification Failed",
          response.message || "OTP verification failed."
        );
      }
    } catch (err) {
      console.error("Verify failed:", err);
      setVerifyFailedMsg(err.message);
      setOptVerifyFailed(true);
      setOtpVerifySuccess(false);
      showPopup(
        "error",
        "OTP Verification Failed",
        err.message || "OTP verification failed."
      );
    }
  };

  return (
    <div>
      <div className="p-10">
        <div
          className="flex justify-end  text-xs"
          style={{ fontFamily: "Calibri" }}
        >
          <p>Home </p>
          <span> / </span>
          <p>customer </p> <span> / </span>
          <TextStyles color="gradient">+ New Onboarding</TextStyles>
        </div>
        <div className="flex justify-start ">
          <div>
            <BackIcon
              onClick={() => {
                navigate("/customer");
              }}
            />
          </div>
          <div className="items-center mt-3">
            <TextStyles color="gradient" style={{ fontSize: "20px" }}>
              New Onboarding
            </TextStyles>
          </div>
        </div>
        <CardsBorder>
          <div className="px-10">
            <h4 className="text-xl">Basic Information </h4>

            <div className="flex flex-col space-y-6">
              {/* Row 1: Name + Username */}
              <div className="flex gap-6">
                <div className="flex-1">
                  <label className="block font-semibold text-gray-500">
                    Name
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={basicInformation.name}
                    onChange={handleChange}
                    className="w-full py-2 border-none focus:outline-none"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>
                <div className="flex-1">
                  <label className="block font-semibold text-gray-500">
                    Username
                  </label>
                  <Input
                    type="text"
                    name="username"
                    value={basicInformation.username}
                    onChange={handleChange}
                    onBlur={() => {
                      if (
                        basicInformation.email &&
                        !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
                          basicInformation.email
                        )
                      ) {
                        setErrors((prev) => ({
                          ...prev,
                          email: "Invalid email address format.",
                        }));
                      }
                    }}
                    className="w-full py-2 border-none focus:outline-none"
                  />
                </div>
              </div>

              {/* Row 2: Email + Send Code + OTP + Verify */}
              <div className="flex items-end gap-4">
                <div>
                  <label className="block font-semibold text-gray-500">
                    Email Id
                  </label>
                  <Input
                    type="email"
                    name="email"
                    placeholder="Email Id"
                    value={basicInformation.email}
                    onChange={handleChange}
                    className="w-56 py-2 border-none focus:outline-none"
                    variant="secondary"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>
                <div className="mt-6">
                  <div
                    className={`rounded-full flex items-center border-2 px-4 cursor-pointer font-medium ${
                      otpSent
                        ? "bg-[#06513D] text-white border-transparent"
                        : "border-[#06513D] text-[#06513D] bg-transparent"
                    }`}
                    onClick={handleSendCode}
                  >
                    <p className="py-2 my-auto px-4">Send Code</p>
                  </div>
                </div>
                <div>
                  <label className="block font-semibold text-gray-500">
                    OTP
                  </label>
                  <Input
                    type="number"
                    name="otp"
                    className="w-28 py-2 border-none focus:outline-none"
                    placeholder="OTP"
                    variant="secondary"
                    value={basicInformation.otp}
                    onChange={handleChange}
                  />
                  {errors.otp && (
                    <p className="text-red-500 text-sm mt-1">{errors.otp}</p>
                  )}
                </div>
                <div className="mt-6">
                  <div
                    className={`rounded-full flex items-center border-2 px-4 cursor-pointer font-medium ${
                      otpVerified
                        ? "bg-[#06513D] text-white border-transparent"
                        : "border-[#06513D] text-[#06513D] bg-transparent"
                    }`}
                    onClick={handleVerify}
                  >
                    <p className="py-2 my-auto px-4">Verify</p>
                  </div>
                </div>
              </div>

              {/* <div className="w-full max-w-md">
                <label className="block font-semibold text-gray-500">
                  Create Password
                </label>
                <Input
                  type="password"
                  name="password"
                  value={basicInformation.password}
                  onChange={handleChange}
                  className="w-full py-2 border-none focus:outline-none"
                />
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                )}
              </div> */}

              <div className="w-full max-w-md">
                <label className="block font-semibold text-gray-500">
                  Create Password
                </label>
                <Input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={basicInformation.password}
                  onChange={handleChange}
                  className={`w-full py-2 border-none focus:outline-none ${
                    errors.password && "text-red-500 text-sm mt-1"
                  }`}
                />
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                )}

                <label className="inline-flex items-center text-sm mt-2">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={showPassword}
                    onChange={() => setShowPassword(!showPassword)}
                  />
                  Show Password
                </label>

                {basicInformation.password && (
                  <div className="mt-2 space-y-1">
                    {[
                      {
                        label: "At least 8 characters",
                        valid: passwordStrength.minLength,
                      },
                      {
                        label: "One uppercase letter",
                        valid: passwordStrength.hasUpperCase,
                      },
                      {
                        label: "One lowercase letter",
                        valid: passwordStrength.hasLowerCase,
                      },
                      {
                        label: "One number",
                        valid: passwordStrength.hasNumbers,
                      },
                      {
                        label: "One special character (@, #, $, etc.)",
                        valid: passwordStrength.hasSpecialChar,
                      },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center text-[0.6rem]">
                        <div
                          className={`w-2 h-2 rounded-full mr-2 flex items-center justify-center ${
                            item.valid ? "bg-[#06513D]" : "bg-gray-300"
                          }`}
                        >
                          {item.valid && (
                            <Check className="w-2 h-2 text-white" />
                          )}
                        </div>
                        <span
                          className={
                            item.valid ? "text-[#06513D]" : "text-gray-500"
                          }
                        >
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Row 3: Location */}
              {/* <div className="w-full max-w-md">
                <label className="block font-semibold text-gray-500">
                  Location
                </label>
                <Input
                  type="text"
                  name="location"
                  value={basicInformation.location}
                  onChange={handleChange}
                  className="w-full py-2 border-none focus:outline-none"
                />
              </div> */}
            </div>
          </div>

          {/* <div className="px-10">
            <h4 className="text-xl">Contact Details</h4>

            <div>
              <div className="flex justify-between gap-5 mb-6">
                <div className="mb-6">
                  <label className="block font-semibold text-gray-500">
                    1. Contact Number
                  </label>
                  <input
                    type="text"
                    name="contactNumber_1"
                    value={basicInformation.contactNumber_1}
                    onChange={handleChange}
                    className="w-64 p-2 border-b focus:outline-none"
                  />
                </div>

                <div className="mb-6">
                  <label className="block font-semibold text-gray-500">
                    Email ID
                  </label>
                  <input
                    type="text"
                    name="emailId_1"
                    value={basicInformation.emailId_1}
                    onChange={handleChange}
                    className="w-64 p-2 border-b focus:outline-none"
                  />
                </div>

                <div>
                  <input
                    type="radio"
                    id="primary_1"
                    name="primaryContact"
                    onChange={() => handlePrimarySelection("contact_1")}
                    checked={
                      basicInformation.primaryContactNumber ===
                      basicInformation.contactNumber_1
                    }
                  />
                  <label htmlFor="primary_1" className="ml-2 text-gray-500">
                    Make it Primary
                  </label>
                </div>
              </div>

              <div className="flex justify-between gap-5 mb-6">
                <div className="mb-6">
                  <label className="block font-semibold text-gray-500">
                    2. Contact Number
                  </label>
                  <input
                    type="text"
                    name="contactNumber_2"
                    value={basicInformation.contactNumber_2}
                    onChange={handleChange}
                    className="w-64 p-2 border-b focus:outline-none"
                  />
                </div>

                <div className="mb-6">
                  <label className="block font-semibold text-gray-500">
                    Email ID
                  </label>
                  <input
                    type="text"
                    name="emailId_2"
                    value={basicInformation.emailId_2}
                    onChange={handleChange}
                    className="w-64 p-2 border-b focus:outline-none"
                  />
                </div>

                <div>
                  <input
                    type="radio"
                    id="primary_2"
                    name="primaryContact"
                    onChange={() => handlePrimarySelection("contact_2")}
                    checked={
                      basicInformation.primaryContactNumber ===
                      basicInformation.contactNumber_2
                    }
                  />
                  <label htmlFor="primary_2" className="ml-2 text-gray-500">
                    Make it Primary
                  </label>
                </div>
              </div>

              <div>
                <div className="mt-6">
                  <div className="w-full bg-[#def5fa] text-[#06513D] rounded-md">
                    <p className="text-center py-2 font-semibold">
                      {" "}
                      + Add Contact Details
                    </p>
                  </div>
                  <div className="w-full bg-[#def5fa] text-[#06513D] rounded-md">
                    <p className="text-center py-2 font-semibold"> Save</p>
                  </div>
                  <div className="py-3">
                    <label className="block font-semibold text-gray-500 pb-4">
                      Description
                    </label>
                    <textarea
                      type="text"
                      name="name"
                      value={basicInformation.description}
                      onChange={handleChange}
                      className="w-full h-24 p-2 border rounded-lg border-black focus:outline-none"
                    />
                  </div>
                </div>
                <div
                  style={{
                    borderRadius: "10px",
                    paddingTop: "20px",
                  }}
                  className="mr-4 mb-4 w-full"
                >
                  <OrganizationDetails />
                  <ContactDetails />
                </div>
              </div>
            </div>
          </div> */}
          <div className="flex justify-center gap-4 py-6">
            <CustomButton
              buttonType="primary"
              variant="cancel"
              buttonText="Cancel"
              className="text-base font-normal h-[50px] w-[200px]"
              onClick={() => navigate("/customer")}
            />
            <CustomButton
              buttonType="primary"
              variant="ok"
              buttonText="Save"
              className="text-base font-normal h-[50px] w-[200px]"
              onClick={handleSave}
            />
          </div>
        </CardsBorder>
      </div>

      {popup.visible && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
          <PopupCard
            type={popup.type}
            title={popup.title}
            message={popup.message}
          />
        </div>
      )}
    </div>
  );
};

export default NewOnBoarding;
